import { collection, addDoc, query, where, orderBy, onSnapshot, updateDoc, doc, writeBatch, Timestamp } from 'firebase/firestore';
import { db } from './firebase';

export type NotificationType =
  | 'like'
  | 'dislike'
  | 'emoji_reaction'
  | 'reply'
  | 'profile_comment'
  | 'profile_like'
  | 'media_like'
  | 'media_comment'
  | 'media_comment_reply'
  | 'media_new_post'
  | 'pvtmate_new_listing'
  | 'activity_join'
  | 'activity_like'
  | 'activity_comment'
  | 'activity_reply';

export interface Notification {
  id: string;
  recipient_id: string;
  sender_id: string;
  sender_username: string;
  type: NotificationType;
  message_id: string;
  message_preview: string;
  thread_code: string;
  thread_type: 'country' | 'general' | 'bug' | 'profile' | 'media' | 'pvtmate' | 'activity';
  emoji?: string;
  profile_id?: string;
  post_id?: string;
  pvtmate_id?: string;
  activity_id?: string;
  read: boolean;
  created_at: { toDate: () => Date };
}

export async function createNotification(data: {
  recipientId: string;
  senderId: string;
  senderUsername: string;
  type: NotificationType;
  messageId: string;
  messagePreview: string;
  threadCode: string;
  threadType: 'country' | 'general' | 'bug' | 'profile' | 'media';
  emoji?: string;
  profileId?: string;
  postId?: string;
}) {
  if (data.recipientId === data.senderId) return;

  try {
    await addDoc(collection(db, 'notifications'), {
      recipient_id: data.recipientId,
      sender_id: data.senderId,
      sender_username: data.senderUsername,
      type: data.type,
      message_id: data.messageId,
      message_preview: data.messagePreview.slice(0, 100),
      thread_code: data.threadCode,
      thread_type: data.threadType,
      ...(data.emoji && { emoji: data.emoji }),
      ...(data.profileId && { profile_id: data.profileId }),
      ...(data.postId && { post_id: data.postId }),
      read: false,
      created_at: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error creating notification:', error);
  }
}

export async function createMediaNotification(data: {
  recipientId: string;
  senderId: string;
  senderUsername: string;
  type: 'media_like' | 'media_comment' | 'media_comment_reply' | 'media_new_post';
  postId: string;
  preview: string;
}) {
  if (data.recipientId === data.senderId) return;

  try {
    await addDoc(collection(db, 'notifications'), {
      recipient_id: data.recipientId,
      sender_id: data.senderId,
      sender_username: data.senderUsername,
      type: data.type,
      message_id: '',
      message_preview: data.preview.slice(0, 100),
      thread_code: '',
      thread_type: 'media',
      post_id: data.postId,
      read: false,
      created_at: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error creating media notification:', error);
  }
}

export async function createPvtmateNotification(data: {
  recipientId: string;
  senderId: string;
  senderUsername: string;
  pvtmateId: string;
  destination: string;
  city: string;
  firstName: string;
}) {
  if (data.recipientId === data.senderId) return;

  try {
    const preview = `${data.firstName} recherche un(e) PVTmate pour ${data.city}, ${data.destination}`;

    await addDoc(collection(db, 'notifications'), {
      recipient_id: data.recipientId,
      sender_id: data.senderId,
      sender_username: data.senderUsername,
      type: 'pvtmate_new_listing',
      message_id: '',
      message_preview: preview,
      thread_code: '',
      thread_type: 'pvtmate',
      pvtmate_id: data.pvtmateId,
      read: false,
      created_at: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error creating pvtmate notification:', error);
  }
}

export async function createActivityNotification(data: {
  recipientId: string;
  senderId: string;
  senderUsername: string;
  type: 'activity_join' | 'activity_like' | 'activity_comment' | 'activity_reply';
  activityId: string;
  activityTitle: string;
}) {
  if (data.recipientId === data.senderId) return;

  try {
    await addDoc(collection(db, 'notifications'), {
      recipient_id: data.recipientId,
      sender_id: data.senderId,
      sender_username: data.senderUsername,
      type: data.type,
      message_id: '',
      message_preview: data.activityTitle.slice(0, 100),
      thread_code: '',
      thread_type: 'activity',
      activity_id: data.activityId,
      read: false,
      created_at: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error creating activity notification:', error);
  }
}

export function subscribeToNotifications(
  userId: string,
  callback: (notifications: Notification[]) => void
) {
  const q = query(
    collection(db, 'notifications'),
    where('recipient_id', '==', userId),
    orderBy('created_at', 'desc')
  );

  let unsubFallback: (() => void) | null = null;

  const unsub = onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
    })) as Notification[];
    callback(notifications);
  }, (error) => {
    console.warn('Notifications: composite index missing, using fallback query', error);
    const fallbackQ = query(
      collection(db, 'notifications'),
      where('recipient_id', '==', userId)
    );
    unsubFallback = onSnapshot(fallbackQ, (snapshot) => {
      const notifications = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      })) as Notification[];
      notifications.sort((a, b) => {
        try {
          return b.created_at.toDate().getTime() - a.created_at.toDate().getTime();
        } catch {
          return 0;
        }
      });
      callback(notifications);
    }, (fallbackError) => {
      console.error('Error listening to notifications (fallback):', fallbackError);
    });
  });

  return () => {
    unsub();
    unsubFallback?.();
  };
}

export async function markNotificationRead(notificationId: string) {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), { read: true });
  } catch (error) {
    console.error('Error marking notification read:', error);
  }
}

export async function markAllNotificationsRead(userId: string, notifications: Notification[]) {
  try {
    const batch = writeBatch(db);
    const unread = notifications.filter(n => !n.read);
    unread.forEach(n => {
      batch.update(doc(db, 'notifications', n.id), { read: true });
    });
    if (unread.length > 0) await batch.commit();
  } catch (error) {
    console.error('Error marking all notifications read:', error);
  }
}
